/** The backend call: POST /v2/chat/web/stream (app/routers/chat.py in esl-chat), read as
 * Server-Sent Events: `progress` while the bot works, then one `answer` or `error`. */

export interface InboundMessage {
  text: string;
  conversation_id: string;
  market: string;
  locale?: string;
  challenge_token: string | null;
}

export interface OutboundMessage {
  conversation_id: string;
  message_id: string;
  outcome: "answered" | "clarify" | "routed" | "refused";
  body: string;
  intent_key: string | null;
  case_id: string | null;
  desk: string | null;
  degraded: boolean;
  sources: { system: string; ref: string; as_of: string }[];
}

export class ChatApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export type ProgressStage = "understanding" | "warming_up" | "looking_up" | "writing";

/** One message over SSE. `onProgress` fires on each stage the backend reports. */
export async function streamMessage(
  apiUrl: string,
  body: InboundMessage,
  onProgress: (stage: ProgressStage) => void,
): Promise<OutboundMessage> {
  let response: Response;
  try {
    response = await fetch(`${apiUrl}/v2/chat/web/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ChatApiError("Sorry, a connection error occurred.", 0);
  }
  if (!response.ok || !response.body) {
    throw new ChatApiError(`Server error (${response.status})`, response.status);
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let end: number;
    while ((end = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const event = /^event: (.*)$/m.exec(block)?.[1];
      const data = /^data: (.*)$/m.exec(block)?.[1];
      if (!event || data === undefined) continue;
      const payload = JSON.parse(data);
      if (event === "progress") onProgress(payload.stage as ProgressStage);
      if (event === "answer") return payload as OutboundMessage;
      if (event === "error") {
        const status = Number(payload.status) || 500;
        const detail = status === 403
          ? "Security check failed. Refresh the page and try again."
          : String(payload.detail || `Server error (${status})`);
        throw new ChatApiError(detail, status);
      }
    }
  }
  throw new ChatApiError("Sorry, the connection closed before an answer arrived.", 0);
}
